import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { supabase } from "@/integrations/supabase/client";
import { X, Plus, Calendar, DollarSign, Clock, Tag, CheckSquare, FileText } from "lucide-react";
import { CaseTooltip } from './CaseTooltip';

interface EnhancedTask {
  id?: string;
  title: string;
  description?: string;
  status: string;
  priority: string;
  due_date?: string;
  assigned_to?: string;
  case_id?: string;
  task_type?: string;
  category?: string;
  estimated_hours?: number;
  actual_hours?: number;
  hourly_rate?: number;
  is_billable?: boolean;
  show_in_portal?: boolean;
  completion_percentage?: number;
  recurring_type?: string;
  recurring_end_date?: string;
  tags?: string[];
  dependencies?: string[];
  milestone?: boolean;
  attachments?: string[];
  notes?: string;
  reminder_date?: string;
  color_tag?: string;
  is_private?: boolean;
  deadline_critical?: boolean;
  auto_time_tracking?: boolean;
  location?: string;
  checklist?: { item: string; completed: boolean }[];
  approval_required?: boolean;
  court_deadline?: boolean;
  statute_deadline?: boolean;
}

interface Case {
  id: string;
  title: string;
  case_number?: string;
  case_number_cnj?: string;
  case_type?: string;
  status?: string;
  court?: string;
  filing_date?: string;
  clients?: { name: string; email: string };
}

interface EnhancedTaskFormProps {
  task?: EnhancedTask | null;
  onSubmit: (data: any) => void;
  onCancel: () => void;
}

export const EnhancedTaskForm = ({ task, onSubmit, onCancel }: EnhancedTaskFormProps) => {
  const [cases, setCases] = useState<Case[]>([]);
  const [newTag, setNewTag] = useState("");
  const [newChecklistItem, setNewChecklistItem] = useState("");
  const [activeTab, setActiveTab] = useState("basic");

  const [formData, setFormData] = useState<EnhancedTask>({
    title: "",
    description: "",
    status: "Pendente",
    priority: "Média",
    due_date: "",
    assigned_to: "",
    case_id: "",
    task_type: "geral",
    category: "geral",
    estimated_hours: 0,
    actual_hours: 0,
    hourly_rate: 0,
    is_billable: false,
    completion_percentage: 0,
    recurring_type: "",
    recurring_end_date: "",
    tags: [],
    dependencies: [],
    milestone: false,
    attachments: [],
    notes: "",
    reminder_date: "",
    color_tag: "#c4ab83",
    is_private: false,
    deadline_critical: false,
    auto_time_tracking: false,
    location: "",
    checklist: [],
    approval_required: false,
    court_deadline: false,
    statute_deadline: false,
    ...task
  });

  const statusOptions = ["Pendente", "Em andamento", "Concluída", "Cancelada"];
  const priorityOptions = ["Baixa", "Média", "Alta"];
  const taskTypeOptions = [
    { value: "geral", label: "Geral" },
    { value: "prazo", label: "Prazo Processual" },
    { value: "reuniao", label: "Reunião" },
    { value: "pesquisa", label: "Pesquisa Jurídica" },
    { value: "audiencia", label: "Audiência" },
    { value: "peticao", label: "Petição" },
    { value: "protocolo", label: "Protocolo" },
    { value: "recurso", label: "Recurso" },
    { value: "contestacao", label: "Contestação" },
    { value: "acordo", label: "Acordo" },
    { value: "diligencia", label: "Diligência" },
    { value: "delegacia", label: "Acompanhamento em Delegacia" },
    { value: "pericia", label: "Perícia" },
    { value: "cumprimento", label: "Cumprimento de Sentença" },
    { value: "sustentacao", label: "Sustentação Oral" },
    { value: "despacho", label: "Despacho" },
    { value: "citacao", label: "Citação/Intimação" }
  ];

  const categoryOptions = [
    "Geral", "Cível", "Criminal", "Trabalhista", "Tributário", 
    "Administrativo", "Constitucional", "Empresarial", "Família", "Imobiliário"
  ];

  const recurringOptions = [
    { value: "none", label: "Não recorrente" },
    { value: "daily", label: "Diário" },
    { value: "weekly", label: "Semanal" },
    { value: "monthly", label: "Mensal" },
    { value: "yearly", label: "Anual" }
  ];

  const colorOptions = [
    { value: "#c4ab83", label: "Dourado (marca)" },
    { value: "#3b82f6", label: "Azul" },
    { value: "#ef4444", label: "Vermelho" },
    { value: "#10b981", label: "Verde" },
    { value: "#f59e0b", label: "Amarelo" },
    { value: "#8b5cf6", label: "Roxo" },
    { value: "#ec4899", label: "Rosa" },
    { value: "#6b7280", label: "Cinza" }
  ];

  useEffect(() => {
    fetchCases();
  }, []);

  useEffect(() => {
    if (task) {
      setFormData({
        title: task.title || "",
        description: task.description || "",
        status: task.status || "Pendente",
        priority: task.priority || "Média",
        due_date: task.due_date || "",
        assigned_to: task.assigned_to || "",
        case_id: task.case_id || "",
        task_type: task.task_type || "geral",
        category: task.category || "geral",
        estimated_hours: task.estimated_hours || 0,
        actual_hours: task.actual_hours || 0,
        hourly_rate: task.hourly_rate || 0,
        is_billable: task.is_billable || false,
        show_in_portal: (task as any).show_in_portal || false,
        completion_percentage: task.completion_percentage || 0,
        recurring_type: task.recurring_type || "",
        recurring_end_date: task.recurring_end_date || "",
        tags: task.tags || [],
        dependencies: task.dependencies || [],
        milestone: task.milestone || false,
        attachments: task.attachments || [],
        notes: task.notes || "",
        reminder_date: task.reminder_date || "",
        color_tag: task.color_tag || "#c4ab83",
        is_private: task.is_private || false,
        deadline_critical: task.deadline_critical || false,
        auto_time_tracking: task.auto_time_tracking || false,
        location: task.location || "",
        checklist: task.checklist || [],
        approval_required: task.approval_required || false,
        court_deadline: task.court_deadline || false,
        statute_deadline: task.statute_deadline || false,
      });
    }
  }, [task]);

  const fetchCases = async () => {
    try {
      const { data, error } = await supabase
        .from('cases')
        .select(`
          id, 
          title, 
          case_number,
          case_number_cnj,
          case_type,
          status,
          court,
          filing_date,
          clients:client_id(id, name, email)
        `)
        .order('title');

      if (error) throw error;
      setCases(data || []);
    } catch (error) {
      console.error('Erro ao buscar processos:', error);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Inclui o que ficou digitado sem apertar "+" (item de checklist ou tag)
    const pendingItem = newChecklistItem.trim();
    const checklist = [...(formData.checklist || []), ...(pendingItem ? [{ item: pendingItem, completed: false }] : [])];
    const pendingTag = newTag.trim();
    const tags = pendingTag && !formData.tags?.includes(pendingTag) ? [...(formData.tags || []), pendingTag] : formData.tags || [];
    
    const submitData = {
      ...formData,
      case_id: formData.case_id === "none" ? null : formData.case_id || null,
      due_date: formData.due_date || null,
      reminder_date: formData.reminder_date || null,
      recurring_end_date: formData.recurring_end_date || null,
      estimated_hours: formData.estimated_hours || null,
      actual_hours: formData.actual_hours || null,
      hourly_rate: formData.hourly_rate || null,
      tags,
      dependencies: formData.dependencies || [],
      attachments: formData.attachments || [],
      checklist
    };
    
    onSubmit(submitData);
  };

  const addTag = () => {
    if (newTag && !formData.tags?.includes(newTag)) {
      setFormData({
        ...formData,
        tags: [...(formData.tags || []), newTag]
      });
      setNewTag("");
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData({
      ...formData,
      tags: formData.tags?.filter(tag => tag !== tagToRemove) || []
    });
  };

  const addChecklistItem = () => {
    if (newChecklistItem.trim()) {
      setFormData({
        ...formData,
        checklist: [...(formData.checklist || []), { item: newChecklistItem, completed: false }]
      });
      setNewChecklistItem("");
    }
  };

  const removeChecklistItem = (index: number) => {
    const newChecklist = [...(formData.checklist || [])];
    newChecklist.splice(index, 1);
    setFormData({
      ...formData,
      checklist: newChecklist
    });
  };

  const toggleChecklistItem = (index: number) => {
    const newChecklist = [...(formData.checklist || [])];
    newChecklist[index] = {
      ...newChecklist[index],
      completed: !newChecklist[index].completed
    };
    setFormData({
      ...formData,
      checklist: newChecklist
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-2 grid h-auto w-full grid-cols-3 gap-1 rounded-xl bg-slate-100 p-1 sm:grid-cols-5">
          <TabsTrigger value="basic" className="rounded-lg text-slate-500 transition-all duration-200 hover:text-slate-800 data-[state=active]:bg-white data-[state=active]:font-semibold data-[state=active]:text-[#7a6440] data-[state=active]:shadow-sm">Básico</TabsTrigger>
          <TabsTrigger value="details" className="rounded-lg text-slate-500 transition-all duration-200 hover:text-slate-800 data-[state=active]:bg-white data-[state=active]:font-semibold data-[state=active]:text-[#7a6440] data-[state=active]:shadow-sm">Detalhes</TabsTrigger>
          <TabsTrigger value="billing" className="rounded-lg text-slate-500 transition-all duration-200 hover:text-slate-800 data-[state=active]:bg-white data-[state=active]:font-semibold data-[state=active]:text-[#7a6440] data-[state=active]:shadow-sm">Faturamento</TabsTrigger>
          <TabsTrigger value="checklist" className="rounded-lg text-slate-500 transition-all duration-200 hover:text-slate-800 data-[state=active]:bg-white data-[state=active]:font-semibold data-[state=active]:text-[#7a6440] data-[state=active]:shadow-sm">Checklist</TabsTrigger>
          <TabsTrigger value="advanced" className="rounded-lg text-slate-500 transition-all duration-200 hover:text-slate-800 data-[state=active]:bg-white data-[state=active]:font-semibold data-[state=active]:text-[#7a6440] data-[state=active]:shadow-sm">Avançado</TabsTrigger>
        </TabsList>

        <TabsContent value="basic" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Informações Básicas
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="title">Título *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="description">Descrição</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="task_type">Tipo de Tarefa</Label>
                  <Select value={formData.task_type} onValueChange={(value) => setFormData({ ...formData, task_type: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {taskTypeOptions.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="category">Categoria</Label>
                  <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categoryOptions.map((category) => (
                        <SelectItem key={category} value={category.toLowerCase()}>
                          {category}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="case_id">Processo Relacionado</Label>
                <Select value={formData.case_id} onValueChange={(value) => setFormData({ ...formData, case_id: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um processo (opcional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhum processo</SelectItem>
                    {cases.map((case_) => (
                      <SelectItem key={case_.id} value={case_.id}>
                        <span className="truncate">
                          {case_.title.length > 30 ? `${case_.title.substring(0, 30)}...` : case_.title}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {/* Mostrar informações do processo selecionado */}
                {formData.case_id && formData.case_id !== "none" && (
                  <div className="mt-2 p-3 bg-blue-50 rounded-lg border border-blue-200 overflow-hidden">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="w-4 h-4 text-blue-600 flex-shrink-0" />
                      <span className="text-sm font-medium text-blue-800">Processo Vinculado:</span>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-gray-700 font-medium">
                        {(() => {
                          const title = cases.find(c => c.id === formData.case_id)?.title || '';
                          return title.length > 30 ? `${title.substring(0, 30)}...` : title;
                        })()}
                      </p>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-600">Número:</span>
                        <CaseTooltip caseData={cases.find(c => c.id === formData.case_id)!}>
                          <button
                            onClick={() => window.location.href = `/processo/${cases.find(c => c.id === formData.case_id)?.id}`}
                            className="text-blue-600 hover:text-blue-800 hover:underline font-mono text-xs transition-colors"
                          >
                            {cases.find(c => c.id === formData.case_id)?.case_number_cnj || cases.find(c => c.id === formData.case_id)?.case_number || 'Sem número'}
                          </button>
                        </CaseTooltip>
                      </div>
                    </div>
                  </div>
                )}
            {formData.case_id && formData.case_id !== "none" && (
              <label className="mt-3 flex cursor-pointer items-start gap-3 rounded-xl border border-[#c4ab83]/40 bg-[#faf7f2] px-3 py-2.5">
                <Switch checked={!!formData.show_in_portal} onCheckedChange={(checked) => setFormData({ ...formData, show_in_portal: checked })} className="mt-0.5" />
                <span className="text-sm">
                  <span className="block font-medium text-slate-800">Mostrar ao cliente no portal</span>
                  <span className="block text-xs text-slate-500">Ao concluir, o título desta tarefa entra como andamento do processo no Portal do Cliente e ele recebe um aviso.</span>
                </span>
              </label>
            )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="status">Status</Label>
                  <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {statusOptions.map((status) => (
                        <SelectItem key={status} value={status}>
                          {status}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="priority">Prioridade</Label>
                  <Select value={formData.priority} onValueChange={(value) => setFormData({ ...formData, priority: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {priorityOptions.map((priority) => (
                        <SelectItem key={priority} value={priority}>
                          {priority}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="details" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Detalhes e Prazos</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                                 <div>
                   <Label htmlFor="due_date">Prazo</Label>
                   <Input
                     id="due_date"
                     type="date"
                     value={formData.due_date}
                     onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                     // Definir data mínima como hoje (Brasil)
                     min={new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' })}
                   />
                 </div>
                
                <div>
                  <Label htmlFor="reminder_date">Lembrete</Label>
                  <Input
                    id="reminder_date"
                    type="datetime-local"
                    value={formData.reminder_date}
                    onChange={(e) => setFormData({ ...formData, reminder_date: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="assigned_to">Responsável</Label>
                  <Input
                    id="assigned_to"
                    value={formData.assigned_to}
                    onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                    placeholder="Nome do responsável"
                  />
                </div>

                <div>
                  <Label htmlFor="location">Local</Label>
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="Local da tarefa"
                  />
                </div>
              </div>


              <div>
                <Label htmlFor="tags">Tags</Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    placeholder="Nova tag"
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  />
                  <Button type="button" onClick={addTag} size="sm">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {formData.tags && formData.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.tags.map((tag, index) => (
                      <Badge key={index} variant="secondary" className="flex items-center gap-1">
                        <Tag className="w-3 h-3" />
                        {tag}
                        <X 
                          className="w-3 h-3 cursor-pointer" 
                          onClick={() => removeTag(tag)}
                        />
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="notes">Notas Adicionais</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  placeholder="Notas e observações sobre a tarefa"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="billing" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Informações de Faturamento
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="is_billable"
                  checked={formData.is_billable}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_billable: checked })}
                />
                <Label htmlFor="is_billable">Tarefa faturável</Label>
              </div>

              {formData.is_billable && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="estimated_hours">Horas Estimadas</Label>
                      <Input
                        id="estimated_hours"
                        type="number"
                        step="0.25"
                        min="0"
                        value={formData.estimated_hours || ''}
                        onChange={(e) => setFormData({ ...formData, estimated_hours: parseFloat(e.target.value) || 0 })}
                      />
                    </div>

                    <div>
                      <Label htmlFor="actual_hours">Horas Trabalhadas</Label>
                      <Input
                        id="actual_hours"
                        type="number"
                        step="0.25"
                        min="0"
                        value={formData.actual_hours || ''}
                        onChange={(e) => setFormData({ ...formData, actual_hours: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="hourly_rate">Valor por Hora (R$)</Label>
                    <Input
                      id="hourly_rate"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.hourly_rate || ''}
                      onChange={(e) => setFormData({ ...formData, hourly_rate: parseFloat(e.target.value) || 0 })}
                    />
                  </div>

                  {formData.actual_hours && formData.hourly_rate && (
                    <div className="p-4 bg-muted rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="font-medium">Valor Total:</span>
                        <span className="text-lg font-bold text-tertiary">
                          {new Intl.NumberFormat('pt-BR', {
                            style: 'currency',
                            currency: 'BRL'
                          }).format(formData.actual_hours * formData.hourly_rate)}
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center space-x-2">
                    <Switch
                      id="auto_time_tracking"
                      checked={formData.auto_time_tracking}
                      onCheckedChange={(checked) => setFormData({ ...formData, auto_time_tracking: checked })}
                    />
                    <Label htmlFor="auto_time_tracking">Rastreamento automático de tempo</Label>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="checklist" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckSquare className="w-5 h-5" />
                Lista de Verificação
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  value={newChecklistItem}
                  onChange={(e) => setNewChecklistItem(e.target.value)}
                  placeholder="Novo item da lista"
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addChecklistItem())}
                />
                <Button type="button" onClick={addChecklistItem} size="sm">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              {formData.checklist && formData.checklist.length > 0 && (
                <div className="space-y-2">
                  {formData.checklist.map((item, index) => (
                    <div key={index} className="flex items-center gap-2 p-2 border rounded">
                      <input
                        type="checkbox"
                        checked={item.completed}
                        onChange={() => toggleChecklistItem(index)}
                        className="rounded"
                      />
                      <span className={item.completed ? "line-through text-muted-foreground" : ""}>
                        {item.item}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeChecklistItem(index)}
                        className="ml-auto"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="advanced" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Configurações Avançadas</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="milestone"
                    checked={formData.milestone}
                    onCheckedChange={(checked) => setFormData({ ...formData, milestone: checked })}
                  />
                  <Label htmlFor="milestone">Marco do projeto</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_private"
                    checked={formData.is_private}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_private: checked })}
                  />
                  <Label htmlFor="is_private">Tarefa privada</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="deadline_critical"
                    checked={formData.deadline_critical}
                    onCheckedChange={(checked) => setFormData({ ...formData, deadline_critical: checked })}
                  />
                  <Label htmlFor="deadline_critical">Prazo crítico</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="approval_required"
                    checked={formData.approval_required}
                    onCheckedChange={(checked) => setFormData({ ...formData, approval_required: checked })}
                  />
                  <Label htmlFor="approval_required">Requer aprovação</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="court_deadline"
                    checked={formData.court_deadline}
                    onCheckedChange={(checked) => setFormData({ ...formData, court_deadline: checked })}
                  />
                  <Label htmlFor="court_deadline">Prazo judicial</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="statute_deadline"
                    checked={formData.statute_deadline}
                    onCheckedChange={(checked) => setFormData({ ...formData, statute_deadline: checked })}
                  />
                  <Label htmlFor="statute_deadline">Prazo prescricional</Label>
                </div>
              </div>

              <Separator />

              <div>
                <Label htmlFor="recurring_type">Recorrência</Label>
                <Select value={formData.recurring_type} onValueChange={(value) => setFormData({ ...formData, recurring_type: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {recurringOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {formData.recurring_type && (
                <div>
                  <Label htmlFor="recurring_end_date">Data Final da Recorrência</Label>
                  <Input
                    id="recurring_end_date"
                    type="date"
                    value={formData.recurring_end_date}
                    onChange={(e) => setFormData({ ...formData, recurring_end_date: e.target.value })}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit">
          {task ? "Atualizar" : "Criar"} Tarefa
        </Button>
      </div>
    </form>
  );
};